import { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { api } from "../lib/api";

const CARD =
  "relative bg-white border border-[#EEEEEE] rounded-xl p-5 " +
  "before:content-[''] before:absolute before:top-0 before:left-0 " +
  "before:right-0 before:h-[3px] before:bg-[#0AADA0] before:rounded-t-xl";
const LABEL = "text-[11px] uppercase tracking-wider text-[#6B7280] mb-2";
const BIG = "font-serif-display text-4xl text-[#0AADA0]";
const SUB = "text-xs text-[#6B7280] mt-1";

const DONUT_COLORS = ["#0AADA0", "#7F77DD", "#E63946", "#5DBB63", "#FF6B4A"];

const arrow = (a, b) => (a === b ? "→" : a > b ? "↑" : "↓");
const colorOf = (a, b) => (a === b ? "#6B7280" : a > b ? "#3BB273" : "#E63946");

// CSV export helper — quotes minimal fields to keep the file portable.
const _csvCell = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const DigestAnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [growthRange, setGrowthRange] = useState("30D");
  const [f, setF] = useState({
    lang: "", island: "", status: "", source: "", search: "",
  });
  const [page, setPage] = useState(1);
  const PAGE = 50;

  useEffect(() => {
    setLoading(true);
    api.get("/admin/digest/analytics")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const subs = data?.subscribers || [];
  const filtered = useMemo(() => {
    const q = f.search.trim().toLowerCase();
    return subs.filter((s) => {
      if (f.lang && s.lang !== f.lang) return false;
      if (f.island) {
        if (!(s.islands || []).includes(f.island) &&
            !(s.islands || []).includes("all")) return false;
      }
      if (f.status === "active" && !s.active) return false;
      if (f.status === "inactive" && s.active) return false;
      if (f.source && s.subscribe_source !== f.source) return false;
      if (q && !(s.phone_masked || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [subs, f]);
  const pageSubs = filtered.slice((page - 1) * PAGE, page * PAGE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));

  const exportCSV = () => {
    const rows = [[
      "Phone (masked)", "Language", "Islands", "Categories",
      "Joined", "Source", "Status",
    ]];
    for (const s of filtered) {
      rows.push([
        s.phone_masked, s.lang, (s.islands || []).join("|"),
        (s.categories || []).join("|"), s.created_at,
        s.subscribe_source, s.active ? "Active" : "Unsubscribed",
      ]);
    }
    const csv = rows.map((r) => r.map(_csvCell).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Loading digest analytics…</div>;
  if (!data) return <div className="text-sm text-[#6B7280]">Analytics unavailable.</div>;

  const langOrder = ["en", "nl", "es"];
  const langNames = { en: "English", nl: "Dutch", es: "Spanish" };
  const langData = langOrder
    .filter((k) => (data.by_lang || {})[k])
    .map((k) => ({ name: langNames[k], value: data.by_lang[k], key: k }));

  const catData = (data.by_category || []).slice(0, 12);
  const sourceEntries = Object.entries(data.by_source || {});

  // Growth-chart windowing
  const days = { "7D": 7, "30D": 30, "90D": 90, "All": 9999 }[growthRange];
  const cutoff = new Date(Date.now() - days * 86400000);
  let running = 0;
  const growthPoints = (data.growth_series || [])
    .map((r) => {
      running += r.new;
      return { date: r.date, total: running, delta: r.new };
    })
    .filter((r) => days >= 9999 || new Date(r.date) >= cutoff);
  const spikes = growthPoints.filter(
    (r, i) => r.delta > 0 && i > 0 &&
              (r.delta / Math.max(growthPoints[i - 1].total, 1)) > 0.1
  );

  return (
    <div className="space-y-6" data-testid="digest-analytics-tab">
      {/* Row 1 — Growth KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={CARD} data-testid="da-total-subs">
          <div className={LABEL}>total subscribers</div>
          <div className={BIG}>{data.total_subscribers}</div>
          <div className="text-xs mt-1" style={{ color: colorOf(data.new_week, data.prev_week) }}>
            {arrow(data.new_week, data.prev_week)} {data.new_week - data.prev_week >= 0 ? "+" : ""}
            {data.new_week - data.prev_week} vs last week
          </div>
        </div>
        <div className={CARD} data-testid="da-new-today">
          <div className={LABEL}>joined today</div>
          <div className={BIG}>{data.new_today}</div>
          <div className={SUB} style={{ color: colorOf(data.new_today, data.new_yesterday) }}>
            {arrow(data.new_today, data.new_yesterday)} vs {data.new_yesterday} yesterday
          </div>
        </div>
        <div className={CARD} data-testid="da-new-week">
          <div className={LABEL}>joined this week</div>
          <div className={BIG}>{data.new_week}</div>
          <div className={SUB} style={{ color: colorOf(data.new_week, data.prev_week) }}>
            {arrow(data.new_week, data.prev_week)} vs {data.prev_week} previous week
          </div>
        </div>
        <div className={CARD} data-testid="da-unsub-week">
          <div className={LABEL}>unsubscribed</div>
          <div className={BIG}>{data.unsub_week}</div>
          <div className={SUB}>{data.churn_pct}% churn rate</div>
        </div>
      </div>

      {/* Row 2 — Engagement KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={CARD} data-testid="da-open-rate">
          <div className={LABEL}>Open rate</div>
          <div className="text-lg text-[#6B7280] font-medium mt-2">
            Tracking coming soon
          </div>
          <div className={SUB}>7-day average</div>
        </div>
        <div className={CARD} data-testid="da-click-rate">
          <div className={LABEL}>Click rate</div>
          <div className={BIG}>{data.click_rate_pct}%</div>
          <div className={SUB}>7-day average · {data.total_clicks_7d} clicks</div>
        </div>
        <div className={CARD} data-testid="da-top-story">
          <div className={LABEL}>Top story clicked</div>
          {data.top_story_clicked ? (
            <a
              href={`/article/${data.top_story_clicked.article_id}-${data.top_story_clicked.slug}`}
              target="_blank" rel="noopener noreferrer"
              className="block text-sm text-[#1A1A1A] hover:text-[#0AADA0] leading-snug mt-1"
            >
              {data.top_story_clicked.headline}
              <div className={SUB}>{data.top_story_clicked.clicks} clicks</div>
            </a>
          ) : (
            <div className="text-sm text-[#6B7280] mt-1">No clicks yet</div>
          )}
        </div>
        <div className={CARD} data-testid="da-by-source">
          <div className={LABEL}>Subscribers by source</div>
          <div className="space-y-1.5 mt-1">
            {sourceEntries.length === 0 && (
              <div className="text-xs text-[#6B7280]">No source data yet</div>
            )}
            {sourceEntries.map(([k, v]) => {
              const max = Math.max(...sourceEntries.map(([, c]) => c), 1);
              return (
                <div key={k} className="flex items-center gap-2 text-[11px]">
                  <span className="w-24 truncate text-[#6B7280]">{k || "(none)"}</span>
                  <div className="flex-1 bg-[#F5F5F5] rounded h-2 overflow-hidden">
                    <div className="h-2 bg-[#0AADA0]" style={{ width: `${(v / max) * 100}%` }} />
                  </div>
                  <span className="w-6 text-right text-[#1A1A1A]">{v}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Row 3 — Segmentation */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={CARD} data-testid="da-by-lang">
          <div className={LABEL}>By language</div>
          <div className="relative h-48">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={langData} innerRadius={45} outerRadius={70}
                  paddingAngle={2} dataKey="value"
                >
                  {langData.map((_, i) => (
                    <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <div className="text-2xl font-serif-display text-[#1A1A1A]">
                  {data.total_subscribers}
                </div>
                <div className="text-[10px] text-[#6B7280]">total</div>
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-3 text-xs text-[#6B7280] flex-wrap mt-2">
            {langData.map((l, i) => (
              <div key={l.key} className="flex items-center gap-1">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }}
                />
                {l.name}: {l.value}
              </div>
            ))}
          </div>
        </div>

        <div className={CARD} data-testid="da-by-island">
          <div className={LABEL}>By island</div>
          <div className="h-48">
            <ResponsiveContainer>
              <BarChart data={data.by_island} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                <Tooltip />
                <Bar dataKey="count">
                  {(data.by_island || []).map((i, idx) => (
                    <Cell key={idx} fill={i.accent_color || "#0AADA0"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={CARD} data-testid="da-by-category">
          <div className={LABEL}>By category preference</div>
          <div className="h-48">
            <ResponsiveContainer>
              <BarChart data={catData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={90} />
                <Tooltip />
                <Bar dataKey="count" fill="#0AADA0" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Digest history */}
      <div className={CARD} data-testid="da-digest-history">
        <h3 className="text-sm font-bold text-[#1A2E35] mb-3">Recent Digest Sends</h3>
        {(data.digest_history || []).length === 0 ? (
          <p className="text-sm text-[#6B7280] text-center py-6">
            No digests sent yet. Your first digest sends at 8am AST tomorrow.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[#6B7280] border-b border-[#F0F0F0]">
                  <th className="py-2">Date</th>
                  <th>Recipients</th>
                  <th>Articles</th>
                  <th>Clicks</th>
                  <th>Click rate</th>
                  <th>Sponsor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(data.digest_history || []).map((h) => {
                  const clicks = Object.values(h.per_article_click_counts || {}).reduce((a, b) => a + b, 0);
                  const rate = h.total_recipients
                    ? ((clicks / h.total_recipients) * 100).toFixed(1) + "%"
                    : "—";
                  const statusColor = h.status === "sent" ? "text-[#3BB273]"
                                     : h.status === "failed" ? "text-[#E63946]"
                                     : "text-[#6B7280]";
                  return (
                    <tr key={h.digest_id} className="border-b border-[#F5F5F5]">
                      <td className="py-2">{(h.send_time || "").slice(0, 16).replace("T", " ")}</td>
                      <td>{h.total_recipients}</td>
                      <td>{(h.articles_included || []).length}</td>
                      <td>{clicks}</td>
                      <td>{rate}</td>
                      <td>{h.sponsor_id ? "yes" : "—"}</td>
                      <td className={statusColor}>{h.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Growth chart */}
      <div className={CARD} data-testid="da-growth-chart">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#1A2E35]">Subscriber growth</h3>
          <div className="flex gap-1">
            {["7D", "30D", "90D", "All"].map((r) => (
              <button
                key={r} type="button"
                onClick={() => setGrowthRange(r)}
                className={`px-2.5 py-1 text-xs rounded-md ${
                  growthRange === r
                    ? "bg-[#0AADA0] text-white"
                    : "bg-[#F5F5F5] text-[#6B7280] hover:bg-[#E5E5E5]"
                }`}
                data-testid={`da-growth-range-${r}`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
        {growthPoints.length === 0 ? (
          <p className="text-sm text-[#6B7280] text-center py-6">
            Showing all available data
          </p>
        ) : (
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={growthPoints}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line
                  type="monotone" dataKey="total" stroke="#0AADA0"
                  strokeWidth={2} dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
            {spikes.length > 0 && (
              <div className="text-[11px] text-[#6B7280] mt-2">
                Growth spikes (&gt;10% daily): {spikes.map((s) => s.date).join(", ")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Subscriber list */}
      <div className={CARD} data-testid="da-subscriber-list">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-bold text-[#1A2E35]">
            All Subscribers
            <span className="ml-2 text-xs text-[#6B7280] font-normal">
              ({filtered.length})
            </span>
          </h3>
          <button
            type="button" onClick={exportCSV}
            className="text-xs px-3 py-1.5 rounded-md bg-[#0AADA0] hover:bg-[#08988c] text-white"
            data-testid="da-export-csv"
          >
            Export CSV
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mb-3 text-xs">
          <input
            placeholder="Search phone…" value={f.search}
            onChange={(e) => { setF({ ...f, search: e.target.value }); setPage(1); }}
            className="border border-[#E5E5E5] rounded px-2 py-1"
            data-testid="da-filter-search"
          />
          <select value={f.lang} onChange={(e) => { setF({ ...f, lang: e.target.value }); setPage(1); }}
                  className="border border-[#E5E5E5] rounded px-2 py-1" data-testid="da-filter-lang">
            <option value="">All languages</option>
            <option value="en">English</option>
            <option value="nl">Dutch</option>
            <option value="es">Spanish</option>
          </select>
          <select value={f.island} onChange={(e) => { setF({ ...f, island: e.target.value }); setPage(1); }}
                  className="border border-[#E5E5E5] rounded px-2 py-1" data-testid="da-filter-island">
            <option value="">All islands</option>
            {(data.by_island || []).map((i) => (
              <option key={i.slug} value={i.slug}>{i.name}</option>
            ))}
          </select>
          <select value={f.status} onChange={(e) => { setF({ ...f, status: e.target.value }); setPage(1); }}
                  className="border border-[#E5E5E5] rounded px-2 py-1" data-testid="da-filter-status">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Unsubscribed</option>
          </select>
          <select value={f.source} onChange={(e) => { setF({ ...f, source: e.target.value }); setPage(1); }}
                  className="border border-[#E5E5E5] rounded px-2 py-1" data-testid="da-filter-source">
            <option value="">All sources</option>
            {["hero_strip", "sidebar", "mobile_bar", "article_page", "in_feed", "digest_forward"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[#6B7280] border-b border-[#F0F0F0]">
                <th className="py-2">Phone</th>
                <th>Language</th>
                <th>Islands</th>
                <th>Categories</th>
                <th>Joined</th>
                <th>Source</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pageSubs.map((s, i) => (
                <tr key={i} className="border-b border-[#F5F5F5]" data-testid={`da-sub-row-${i}`}>
                  <td className="py-2 font-mono">{s.phone_masked}</td>
                  <td>{s.lang}</td>
                  <td>{(s.islands || []).join(", ")}</td>
                  <td>{(s.categories || []).join(", ")}</td>
                  <td>{(s.created_at || "").slice(0, 10)}</td>
                  <td>{s.subscribe_source || "—"}</td>
                  <td>
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          s.active ? "bg-[#3BB273]" : "bg-[#9CA3AF]"
                        }`}
                      />
                      {s.active ? "Active" : "Unsubscribed"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-3 text-xs">
            <button
              disabled={page === 1} onClick={() => setPage(page - 1)}
              className="px-2 py-1 rounded bg-[#F5F5F5] disabled:opacity-40"
            >
              ← Prev
            </button>
            <span className="px-2 py-1 text-[#6B7280]">
              Page {page} / {totalPages}
            </span>
            <button
              disabled={page === totalPages} onClick={() => setPage(page + 1)}
              className="px-2 py-1 rounded bg-[#F5F5F5] disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
